import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { BrandPage, ProductPage } from '@/domain/explore.types'
import { CLASSIFICATION_BOOTSTRAP_KEY } from '@/hooks/useClassification'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { BrandCreate, BrandUpdate, ProductCreate, ProductUpdate } from '@/services/explore.service'
import { exploreApi } from '@/services/explore.service'

/**
 * Writing to the catalog: creating and editing brands and products.
 *
 * Separate from `useExplore` because that file is the READ surface — the funnel, the pages,
 * the assets each one resolves — and the two have no shared state beyond the query keys
 * these invalidate.
 */

/**
 * Edit a brand's profile. Optimistic against the brand page the editor is looking at:
 * the fields being changed are the fields it renders, so the patch is exact and the
 * rollback is a straight restore.
 */
export const useUpdateBrand = (brandId: string, market?: string | null) => {
	const qc = useQueryClient()
	const key = ['brand-page', brandId, market ?? null]
	return useMutation({
		mutationFn: (body: BrandUpdate) => exploreApi.updateBrand(brandId, body),
		onMutate: async (body) => {
			await qc.cancelQueries({ queryKey: key })
			const previous = qc.getQueryData<BrandPage>(key)
			qc.setQueryData<BrandPage>(key, (current) =>
				current ? { ...current, brand: { ...current.brand, ...body } } : current,
			)
			return { previous }
		},
		// Unconditional: any failure means the server never took the edit, whatever it
		// answered, so the cache must go back to what it knew.
		onError: (error, _body, context) => {
			if (context?.previous) qc.setQueryData(key, context.previous)
			notification.error(apiErrorMessage(error, 'Could not save the brand.'))
		},
		onSuccess: () => notification.success('Brand saved.'),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: key })
			void qc.invalidateQueries({ queryKey: ['brands'] })
		},
	})
}

export const useUpdateProduct = (productId: string, market?: string | null) => {
	const qc = useQueryClient()
	const key = ['product-page', productId, market ?? null]
	return useMutation({
		mutationFn: (body: ProductUpdate) => exploreApi.updateProduct(productId, body),
		onMutate: async (body) => {
			await qc.cancelQueries({ queryKey: key })
			const previous = qc.getQueryData<ProductPage>(key)
			qc.setQueryData<ProductPage>(key, (current) =>
				current ? { ...current, product: { ...current.product, ...body } } : current,
			)
			return { previous }
		},
		onError: (error, _body, context) => {
			if (context?.previous) qc.setQueryData(key, context.previous)
			notification.error(apiErrorMessage(error, 'Could not save the product.'))
		},
		onSuccess: () => notification.success('Product saved.'),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: key })
			void qc.invalidateQueries({ queryKey: ['products'] })
		},
	})
}

/**
 * Create a brand. Nothing to patch — the row does not exist until the server allocates it,
 * and its id is what the caller needs to navigate to — so this only invalidates.
 */
export const useCreateBrand = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: BrandCreate) => exploreApi.createBrand(body),
		onError: (error) => notification.error(apiErrorMessage(error, 'Could not create the brand.')),
		onSuccess: (brand) => notification.success(`Brand “${brand.name}” created.`),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: ['brands'] })
			void qc.invalidateQueries({ queryKey: CLASSIFICATION_BOOTSTRAP_KEY })
		},
	})
}

export const useCreateProduct = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: ProductCreate) => exploreApi.createProduct(body),
		onError: (error) => notification.error(apiErrorMessage(error, 'Could not create the product.')),
		onSuccess: (product) => notification.success(`Product “${product.name}” created.`),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: ['products'] })
			void qc.invalidateQueries({ queryKey: ['brand-page'] })
			void qc.invalidateQueries({ queryKey: CLASSIFICATION_BOOTSTRAP_KEY })
		},
	})
}
